import { Form, FormRenderProps } from 'react-final-form';
import { FormApi, Mutator } from 'final-form';
import arrayMutators from 'final-form-arrays';
import { Box, Unstable_Grid2 as Grid } from '@mui/material';
import { useCallback } from 'react';

import {
  CurrencyInputField,
  LoadingOverlay,
  ProductGroupSelectField,
  TextField,
  Typography,
} from '@occc-app/design-system';
import { AwardCount } from '@occc-app/routes/CampaignComponents/components/AwardCount';
import { Flow_Types } from '@occc-app/offerHelpers/types';
import { OfferForm } from '@occc-app/offerHelpers/components/OfferForm';
import { EitherActivationOrAwardCount } from '@occc-app/routes/Promotion/components/PromotionConfigureForm/EitherActivationOrAwardCountProps';
import { CustomData } from '@occc-app/routes/CampaignComponents/components/CustomData/CustomData';
import { CampaignComponentFooterActions } from '@occc-app/routes/CampaignComponents/components/CampaignComponentFooterActions';
import { CampaignComponentReadOnlyFooterActions } from '@occc-app/routes/CampaignComponents/components/CampaignComponentReadOnlyFooterActions';
import { OfferEarningMechanismConfigurationSection } from '@occc-app/offerHelpers/components/offer-earning-mechanism-configuration.section';
import { OfferConfigurationSection } from '@occc-app/offerHelpers/components/offer-configuration-section';
import { OfferConfigurationWizardFormLayout } from '@occc-app/routes/CampaignComponents/components/offer-configuration-wizard-form-layout';
import { OfferNameAndDescriptionConfiguration } from '@occc-app/routes/Offer/components/OfferNameAndDescriptionConfiguration';
import { FulfillmentWindow } from '@occc/feat-constraints';

import { validateStoredValueConfiguration } from './validation';
import {
  StoredValueConfigureFormInitialValues,
  StoredValueConfigureFormValues,
} from './stored-value-configure-form.types';

type StoredValueMutator = Mutator<
  StoredValueConfigureFormValues,
  StoredValueConfigureFormInitialValues
>;

const setFieldValueMutator: StoredValueMutator = ([name, value], state, { changeValue }) => {
  changeValue(state, name, () => value);
};

const parseNumericValue = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return Number.isNaN(value) ? undefined : Math.abs(value);
  }

  if (typeof value === 'string') {
    const parsedValue = parseFloat(value);
    return Number.isNaN(parsedValue) ? undefined : Math.abs(parsedValue);
  }

  return undefined;
};

type StoredValueConfigureFormProps = {
  onSubmit: (
    values: StoredValueConfigureFormValues,
    form: FormApi<StoredValueConfigureFormValues, StoredValueConfigureFormInitialValues>,
  ) => void;
  onCancelClick: (hasUnsavedChanges?: boolean) => void;
  initialValues: StoredValueConfigureFormInitialValues;
  loading: boolean;
  currentFlowType: Flow_Types;
  onNextStepButtonClick?: () => void;
  isFormEditable: boolean;
};

export const StoredValueConfigureForm = ({
  onSubmit,
  initialValues,
  onCancelClick,
  loading,
  currentFlowType,
  onNextStepButtonClick,
  isFormEditable,
}: StoredValueConfigureFormProps) => {
  const renderForm = useCallback(
    ({
      handleSubmit,
      submitting,
      values,
      form,
      dirty,
    }: FormRenderProps<StoredValueConfigureFormValues, StoredValueConfigureFormInitialValues>) => {
      const formFooterActionButtons = isFormEditable ? (
        <CampaignComponentFooterActions
          onCancel={() => onCancelClick(dirty)}
          currentFlowType={currentFlowType}
          isSubmitDisabled={submitting || (currentFlowType === Flow_Types.Update && !dirty)}
        />
      ) : (
        <CampaignComponentReadOnlyFooterActions
          onCancel={onCancelClick}
          onNextStepClick={onNextStepButtonClick}
        />
      );

      return (
        <OfferForm dirty={dirty} handleSubmit={handleSubmit}>
          <OfferConfigurationWizardFormLayout actions={formFooterActionButtons}>
            <OfferEarningMechanismConfigurationSection>
              <Box mb={2}>
                <OfferNameAndDescriptionConfiguration
                  entityName='Program'
                  title='Set a Name for This Program'
                  visibilityHint={'Name Will be Displayed on Receipts'}
                />
              </Box>
              <Grid container spacing={3}>
                <Grid xs={12} sm={6} md={3}>
                  <Typography variant='subtitle1' paragraph>
                    Give Members
                  </Typography>
                  <CurrencyInputField
                    name='storedValueDiscount.moneyPerConversion'
                    label='Dollar Amount'
                    valueType='cents'
                  />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <Typography variant='subtitle1' paragraph>
                    For Every
                  </Typography>
                  <TextField
                    name='storedValueDiscount.pointsPerConversion'
                    label='Number of Points'
                    type='number'
                    fieldProps={{
                      parse: parseNumericValue,
                    }}
                  />
                </Grid>
                <Grid xs={12}>
                  <Typography variant='subtitle1' paragraph>
                    On all but the following product group(s)
                  </Typography>
                  <ProductGroupSelectField
                    inputProps={{
                      margin: 'none',
                    }}
                    name='storedValueDiscount.excludedProductGroups'
                    label='Excluded product groups'
                  />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <Typography variant='subtitle1' paragraph>
                    Minimum Purchase
                  </Typography>
                  <TextField
                    name='storedValueDiscount.minPointsPerConversion'
                    label='Number of Points'
                    type='number'
                    fieldProps={{
                      parse: parseNumericValue,
                    }}
                  />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <Typography variant='subtitle1' paragraph>
                    Maximum Purchase
                  </Typography>
                  <TextField
                    name='storedValueDiscount.maxPointsPerConversion'
                    label='Number of Points'
                    type='number'
                    fieldProps={{
                      parse: parseNumericValue,
                    }}
                  />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <Typography variant='subtitle1' paragraph>
                    Expiration
                  </Typography>
                  <TextField
                    name='storedValueDiscount.expirationDays'
                    label='Number of Days'
                    type='number'
                    fieldProps={{
                      parse: parseNumericValue,
                    }}
                  />
                </Grid>
              </Grid>
            </OfferEarningMechanismConfigurationSection>

            <OfferConfigurationSection title='Limits'>
              <AwardCount values={values} />
            </OfferConfigurationSection>

            <OfferConfigurationSection title='Requirements'>
              <FulfillmentWindow values={values} />
            </OfferConfigurationSection>

            <OfferConfigurationSection title='Advanced Offer Settings' mb={0}>
              <CustomData values={values.customData} />
            </OfferConfigurationSection>
          </OfferConfigurationWizardFormLayout>
          <EitherActivationOrAwardCount form={form} />
        </OfferForm>
      );
    },
    [isFormEditable, currentFlowType, onCancelClick, onNextStepButtonClick],
  );

  return (
    <Box position='relative'>
      <Form<StoredValueConfigureFormValues, StoredValueConfigureFormInitialValues>
        onSubmit={onSubmit}
        render={renderForm}
        mutators={
          {
            ...arrayMutators,
            setFieldValue: setFieldValueMutator,
          } as unknown as Record<string, StoredValueMutator>
        }
        initialValues={initialValues}
        validate={validateStoredValueConfiguration}
      />
      {loading && <LoadingOverlay rounded />}
    </Box>
  );
};
